import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppShell from './components/AppShell';
import { ProtectedRoute } from './components/ProtectedRoute';
import AcessoNegadoPage from './pages/AcessoNegadoPage';
import LoginPage from './pages/LoginPage';
import TrocarSenhaPage from './pages/TrocarSenhaPage';
import CotacoesListPage from './features/cotacoes/CotacoesListPage';
import CotacaoPage from './features/cotacoes/CotacaoPage';
import EqualizacaoPage from './features/cotacoes/EqualizacaoPage';
import ImportarCotacaoPage from './features/cotacoes/ImportarCotacaoPage';
import PropostasPage from './features/cotacoes/PropostasPage';
import IndicesPage from './features/indices/IndicesPage';
import AnalisePage from './features/cotacoes/AnalisePage';
import FechamentoPage from './features/cotacoes/FechamentoPage';
import ProdutosPage from './features/produtos/ProdutosPage';
import ProdutoModal from './features/produtos/ProdutoModal';
import ImportarItensErpPage from './features/produtos/ImportarItensErpPage';
import FornecedoresPage from './features/fornecedores/FornecedoresPage';
import FornecedorPage from './features/fornecedores/FornecedorPage';
import UsuariosPage from './features/usuarios/UsuariosPage';
import InicioPage from './features/inicio/InicioPage';
import FolhasPage from './features/folha/FolhasPage';
import ImportarFolhaPage from './features/folha/ImportarFolhaPage';
import FolhaPage from './features/folha/FolhaPage';
import RubricasPage from './features/folha/RubricasPage';
import ImpostosPage from './features/folha/ImpostosPage';
import ConferenciaPage from './features/folha/ConferenciaPage';
import LancamentosPage from './features/folha/LancamentosPage';
import CentrosCustoPage from './features/folha/CentrosCustoPage';
import { useAuthStore } from './stores/authStore';

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

function RotaPadrao() {
  const isAuthenticated = useAuthStore((estado) => estado.isAuthenticated);
  const user = useAuthStore((estado) => estado.user);

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  if (user.trocar_senha) {
    return <Navigate to="/trocar-senha" replace />;
  }

  // Endereço desconhecido cai na página inicial, que se ajusta ao acesso de cada um.
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/acesso-negado" element={<AcessoNegadoPage />} />
          <Route
            path="/trocar-senha"
            element={
              <ProtectedRoute>
                <TrocarSenhaPage />
              </ProtectedRoute>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* Fora de qualquer módulo: cada bloco da página se esconde para quem não alcança. */}
            <Route index element={<InicioPage />} />
            <Route
              path="/cotacoes"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.ver">
                  <CotacoesListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cotacoes/importar"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.importar_planilha">
                  <ImportarCotacaoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cotacoes/:cotacaoId"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.ver">
                  <CotacaoPage />
                </ProtectedRoute>
              }
            />
            {/* O produto abre por cima da lista: conferir o de-para é trabalho de
                fila, e sair da tela a cada um perderia a busca e a posição. */}
            <Route
              path="/produtos"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.ver">
                  <ProdutosPage />
                </ProtectedRoute>
              }
            >
              <Route path=":produtoId" element={<ProdutoModal />} />
            </Route>
            <Route
              path="/produtos/importar"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cadastro.produtos">
                  <ImportarItensErpPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/indices"
              element={
                <ProtectedRoute modulo="indices" permissao="indices.ver">
                  <IndicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cotacoes/:cotacaoId/propostas"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.ver">
                  <PropostasPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cotacoes/:cotacaoId/analise"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.equalizar">
                  <AnalisePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cotacoes/:cotacaoId/equalizacao"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.equalizar">
                  <EqualizacaoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cotacoes/:cotacaoId/fechamento"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.ver">
                  <FechamentoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/folha"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.ver">
                  <FolhasPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/folha/importar"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.importar">
                  <ImportarFolhaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/folha/:folhaId"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.ver">
                  <FolhaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rubricas-e-verbas"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.ver">
                  <RubricasPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/folha/:folhaId/impostos"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.ver">
                  <ImpostosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/folha/:folhaId/totalizador"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.ver">
                  <ImpostosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/folha/:folhaId/conferencia"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.ver">
                  <ConferenciaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/folha/:folhaId/planilha"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.ver">
                  <LancamentosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/centros-de-custo"
              element={
                <ProtectedRoute modulo="folha" permissao="folha.configurar">
                  <CentrosCustoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fornecedores"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cotacao.ver">
                  <FornecedoresPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fornecedores/:fornecedorId"
              element={
                <ProtectedRoute modulo="cotacao" permissao="cadastro.fornecedores">
                  <FornecedorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/usuarios"
              element={
                <ProtectedRoute permissao="usuarios.gerenciar">
                  <UsuariosPage />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<RotaPadrao />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
